import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  STATUS, STATUS_ORDER, SOURCES, WEBSITE_STATES, fmtEUR, fmtDate, labelOf,
} from "@/lib/constants";
import { Search, Phone, Loader2, Inbox, Filter, Upload } from "lucide-react";
import { ImportLeadDialog } from "@/components/ImportLeadDialog";

export default function Leads() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(params.get("status") || "all");
  const [source, setSource] = useState("all");
  const [websiteState, setWebsiteState] = useState("all");
  const [followupDue, setFollowupDue] = useState(false);
  const [sort, setSort] = useState("created_desc");
  const [importOpen, setImportOpen] = useState(false);

  const query = useMemo(() => {
    const q = new URLSearchParams();
    if (search) q.set("search", search);
    if (status !== "all") q.set("status", status);
    if (source !== "all") q.set("source", source);
    if (websiteState !== "all") q.set("website_state", websiteState);
    if (followupDue) q.set("followup_due", "true");
    q.set("sort", sort);
    return q.toString();
  }, [search, status, source, websiteState, followupDue, sort]);

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ["leads", query],
    queryFn: () => api.get(`/leads?${query}`).then((r) => r.data),
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 fade-in">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Leads</h1>
          <p className="text-sm text-muted-foreground mt-1">{leads.length} Einträge</p>
        </div>
        <Button variant="secondary" onClick={() => setImportOpen(true)} data-testid="import-lead-btn" className="gap-1.5">
          <Upload className="h-4 w-4" /> Lead importieren
        </Button>
      </div>

      <ImportLeadDialog open={importOpen} onOpenChange={setImportOpen} />

      {/* Filter bar */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="lead-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Firma, Ansprechpartner, Telefon oder E-Mail suchen …" className="pl-9" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-auto min-w-[130px] h-9" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS[s].label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="w-auto min-w-[120px] h-9" data-testid="filter-source"><SelectValue placeholder="Quelle" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Quellen</SelectItem>
              {SOURCES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={websiteState} onValueChange={setWebsiteState}>
            <SelectTrigger className="w-auto min-w-[140px] h-9" data-testid="filter-website"><SelectValue placeholder="Website-Zustand" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Zustände</SelectItem>
              {WEBSITE_STATES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant={followupDue ? "default" : "secondary"} size="sm" className="h-9"
            data-testid="filter-followup-due" onClick={() => setFollowupDue((v) => !v)}>
            <Filter className="h-3.5 w-3.5 mr-1" /> Follow-up fällig
          </Button>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-auto min-w-[150px] h-9 ml-auto" data-testid="sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="created_desc">Neueste Leads</SelectItem>
              <SelectItem value="followup">Nächstes Follow-up</SelectItem>
              <SelectItem value="last_contact">Letzter Kontakt</SelectItem>
              <SelectItem value="value_desc">Potenzieller Wert</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : leads.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center">
          <Inbox className="h-8 w-8 mx-auto text-muted-foreground/50" />
          <p className="mt-2 text-sm text-muted-foreground">Keine Leads gefunden.</p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block rounded-xl border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Firma</TableHead>
                  <TableHead>Branche</TableHead>
                  <TableHead>Ansprechpartner</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Telefon</TableHead>
                  <TableHead>Website</TableHead>
                  <TableHead>Letzter Kontakt</TableHead>
                  <TableHead>Nächster</TableHead>
                  <TableHead className="text-right">Wert</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((l) => (
                  <TableRow key={l.id} data-testid={`lead-row-${l.id}`}
                    className="cursor-pointer" onClick={() => navigate(`/leads/${l.id}`)}>
                    <TableCell className="font-medium">{l.company}</TableCell>
                    <TableCell className="text-muted-foreground">{l.industry || "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{l.contact_person || "—"}</TableCell>
                    <TableCell><StatusBadge status={l.status} /></TableCell>
                    <TableCell className="text-muted-foreground">{l.phone || "—"}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">{labelOf(WEBSITE_STATES, l.website_state)}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">{l.last_contact_at ? fmtDate(l.last_contact_at) : "—"}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">{l.next_followup_at ? fmtDate(l.next_followup_at) : "—"}</TableCell>
                    <TableCell className="text-right font-medium">{fmtEUR(l.potential_value)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {leads.map((l) => (
              <div key={l.id} data-testid={`lead-card-${l.id}`}
                onClick={() => navigate(`/leads/${l.id}`)}
                className="rounded-xl border border-border bg-card p-4 active:bg-accent transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{l.company}</div>
                    <div className="text-xs text-muted-foreground">{l.industry || "—"} · {l.contact_person || "—"}</div>
                  </div>
                  <StatusBadge status={l.status} />
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-sm font-medium">{fmtEUR(l.potential_value)}</span>
                  {l.phone && (
                    <a href={`tel:${l.phone}`} onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-primary/15 text-primary px-3 py-1.5 text-sm font-medium">
                      <Phone className="h-3.5 w-3.5" /> Anrufen
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
